import { AppButton } from "components/button/AppButton";
import { useGetPackage } from "../hooks/useGetPackage";
import { useGetSubscriptionRules } from "../hooks/useGetSubscriptionRules";
import { Dropdown, Menu, Select, Skeleton, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { AddSubscriptionRule } from "./AddSubscriptionRule";
import { UpdateSubscriptionRule } from "./UpdateSubscriptionRule";
import { TSubscriptionRule } from "../types";

export const SubscriptionRules = () => {
  const [page, setPage] = useState(1);
  const [packageFilter, setPackageFilter] = useState<string | undefined>();
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<TSubscriptionRule | null>(
    null
  );

  const { data: packages } = useGetPackage();
  const { data, isLoading, isFetching } = useGetSubscriptionRules(
    page,
    packageFilter
  );

  const columns: ColumnsType<TSubscriptionRule> = [
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "Feature",
      dataIndex: "feature",
      key: "feature",
    },
    {
      title: "Count",
      dataIndex: "count",
      key: "count",
      width: 90,
    },
    {
      title: "Package",
      key: "package",
      render: (_, record) =>
        record.package
          ? `${record.package.name} (${record.package.account_type ?? "—"})`
          : "—",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "",
      key: "action",
      width: 48,
      render: (_, record) => (
        <Dropdown
          trigger={["click"]}
          overlay={
            <Menu>
              <Menu.Item
                key="edit"
                onClick={() => {
                  setSelectedRule(record);
                  setEditOpen(true);
                }}
              >
                <i className="ri-edit-2-fill text-md"></i> Edit
              </Menu.Item>
            </Menu>
          }
        >
          <i className="ri-more-2-fill text-lg cursor-pointer"></i>
        </Dropdown>
      ),
    },
  ];

  const onFilterPackage = (value: string | undefined) => {
    setPackageFilter(value);
    setPage(1);
  };

  return (
    <div>
      {addOpen && (
        <AddSubscriptionRule
          open={addOpen}
          handleClose={() => setAddOpen(false)}
          packages={packages}
        />
      )}
      {editOpen && selectedRule && (
        <UpdateSubscriptionRule
          open={editOpen}
          handleClose={() => {
            setEditOpen(false);
            setSelectedRule(null);
          }}
          packages={packages}
          data={{ id: selectedRule.id }}
        />
      )}

      <div className="flex flex-col gap-4 md:flex-row md:justify-between md:items-end mb-4">
        <div>
          <h4 className="font-semibold text-lg text-primary">
            Subscription rules
          </h4>
          <p className="text-sm text-gray-600">
            Limits and quotas per package (labels used for usage tracking)
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="min-w-[14rem]">
            <span className="text-xs text-gray-500 block mb-1">Package</span>
            <Select
              className="w-full"
              placeholder="All packages"
              allowClear
              value={packageFilter}
              onChange={(v) => onFilterPackage(v)}
              showSearch
              optionFilterProp="children"
            >
              {packages?.map((p) => (
                <Select.Option key={p.id} value={p.id}>
                  {p.name} ({p.account_type})
                </Select.Option>
              ))}
            </Select>
          </div>
          <AppButton
            type="button"
            label="Add rule"
            isLoading={false}
            handleClick={() => setAddOpen(true)}
          />
        </div>
      </div>
      <hr />

      <div className="bg-white border rounded-md mt-8 px-3 py-5">
        <Skeleton active loading={isLoading && !data}>
          <Table<TSubscriptionRule>
            rowKey="id"
            scroll={{ x: 720 }}
            loading={isFetching}
            columns={columns}
            dataSource={data?.data ?? []}
            pagination={{
              current: data?.current_page ?? page,
              pageSize: data?.per_page ?? 20,
              total: data?.total ?? 0,
              showSizeChanger: false,
              onChange: (p) => setPage(p),
            }}
          />
        </Skeleton>
      </div>
    </div>
  );
};
